import {DataService} from '../../../api';
import {useNotificationsStore} from '../notifications';
import {translate} from '../../../plugins/i18n/i18n';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function isInvisibleMailChar(char) {
  const code = char.codePointAt(0);
  return (
    code <= 31 ||
    code === 127 ||
    (code >= 0x200b && code <= 0x200d) ||
    code === 0xfeff
  );
}

function normalizeMail(value) {
  if (value == null) return undefined;
  const cleaned = Array.from(String(value))
    .filter((char) => !isInvisibleMailChar(char))
    .join('')
    .trim();
  return cleaned || undefined;
}

function parseMailList(value) {
  const mail = normalizeMail(value);
  if (!mail) return {ok: true, mail: undefined};
  if (/[^A-Za-z0-9._%+\-@,]/.test(mail)) return {ok: false, mail: undefined};
  const parts = mail.split(',');
  const addresses = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    if (!part) {
      if (index === parts.length - 1) continue;
      return {ok: false, mail: undefined};
    }
    if (!EMAIL_RE.test(part)) return {ok: false, mail: undefined};
    addresses.push(part);
  }
  if (!addresses.length) return {ok: false, mail: undefined};
  return {ok: true, mail: addresses.join(',')};
}

function isMailValid(value) {
  const parsed = parseMailList(value);
  return parsed.ok && !!parsed.mail;
}

function notifyInvalidMail() {
  useNotificationsStore().insertBannerNotification(
    translate('hit-app.configuration.forward-bookkeeping-invalid-email'),
  );
}

function rejectInvalidMail(value) {
  const parsed = parseMailList(value);
  if (!parsed.ok) {
    notifyInvalidMail();
    return {ok: false, mail: undefined};
  }
  return parsed;
}

export default class BookkeepingForwardConfig {
  constructor() {
    this.active = undefined;
    this.type = undefined;
    this.generalMail = undefined;
    this.summaryActive = undefined;
    this.summaryMail = undefined;
    this.journalConfig = {};
    this.generalMailRevision = 0;
    this.summaryMailRevision = 0;
    this.journalMailRevision = {};
  }

  bumpMailField(field, journalId) {
    if (field === 'general') {
      this.generalMailRevision += 1;
    } else if (field === 'summary') {
      this.summaryMailRevision += 1;
    } else if (field === 'journal' && journalId) {
      this.journalMailRevision = {
        ...this.journalMailRevision,
        [journalId]: (this.journalMailRevision[journalId] || 0) + 1,
      };
    }
  }

  processInitConfig(configLabel, configValue) {
    if (configLabel === 'bookkeeping.forward.general') {
      const mail = configValue?.mail ?? configValue?.email;
      const active =
        typeof configValue?.active === 'boolean'
          ? configValue.active
          : undefined;
      // Backward compatibility:
      // - Some instances stored only the mail (no type).
      // - Some instances stored a type without an explicit active flag.
      this.active = active ?? !!mail;
      this.type = configValue?.type ?? (mail ? 'EMAIL' : 'NONE');
      this.generalMail = mail;
    } else if (configLabel === 'bookkeeping.forward.summary') {
      const mail = configValue?.mail ?? configValue?.email;
      const active =
        typeof configValue?.active === 'boolean'
          ? configValue.active
          : undefined;
      this.summaryActive = active ?? false;
      this.summaryMail = mail;
    } else if (configLabel === 'bookkeeping.forward.journal') {
      (configValue ?? []).forEach((j) => {
        const journalId = j?.journal_id;
        if (!journalId) return;
        this.journalConfig[journalId] = {
          ...j,
          mail: j?.mail ?? j?.email,
        };
      });
    }
  }

  get hasForwardActivated() {
    if (typeof this.active === 'boolean') return this.active;
    return this.type && this.type !== 'NONE';
  }

  /** GENERAL CONFIGURATION FUNCTIONS */

  isMailValid(value) {
    return isMailValid(value);
  }

  /** Sets active flag in the object and updates the config in the database */
  setActive(enabled) {
    if (enabled && !isMailValid(this.generalMail)) {
      notifyInvalidMail();
      return;
    }
    this.active = !!enabled;
    // Keep type for backward compatibility & WINBOOKS behavior, but deactivate by setting NONE.
    if (!this.active) {
      this.type = 'NONE';
    } else if (!this.type || this.type === 'NONE') {
      this.type = 'EMAIL';
    }
    this.updateGeneralConfig();
  }

  /** Legacy helper: keep existing callers working (maps type to active). */
  setType(selectedType) {
    if (selectedType !== 'NONE' && !isMailValid(this.generalMail)) {
      notifyInvalidMail();
      return;
    }
    this.type = selectedType;
    this.active = selectedType !== 'NONE';
    if (selectedType === 'NONE') {
      this.generalMail = undefined;
    }
    this.updateGeneralConfig();
  }

  /** Sets the general mail in the object and updates the config in the database */
  setGeneralMail(selectedMail) {
    const {ok, mail} = rejectInvalidMail(selectedMail);
    if (!ok) {
      this.bumpMailField('general');
      return;
    }
    if (this.hasForwardActivated && !mail) {
      notifyInvalidMail();
      this.bumpMailField('general');
      return;
    }
    this.generalMail = mail;
    this.updateGeneralConfig();
  }

  /** Function used to update the general bookkeeping forwarding config in the database */
  updateGeneralConfig() {
    const newConfig = {
      active: this.hasForwardActivated,
      type: this.type,
      mail: this.generalMail,
    };
    DataService.update(
      'config',
      {label: 'eq.bookkeeping.forward.general'},
      {value: newConfig},
    );
  }

  /** JOURNAL CONFIGURATION FUNCTIONS */

  /** SUMMARY CONFIGURATION FUNCTIONS */

  setSummaryActive(enabled) {
    if (enabled && !isMailValid(this.summaryMail)) {
      notifyInvalidMail();
      return;
    }
    this.summaryActive = !!enabled;
    this.updateSummaryConfig();
  }

  setSummaryMail(selectedMail) {
    const {ok, mail} = rejectInvalidMail(selectedMail);
    if (!ok) {
      this.bumpMailField('summary');
      return;
    }
    if (this.summaryActive && !mail) {
      notifyInvalidMail();
      this.bumpMailField('summary');
      return;
    }
    this.summaryMail = mail;
    this.updateSummaryConfig();
  }

  updateSummaryConfig() {
    const newConfig = {
      active: !!this.summaryActive,
      mail: this.summaryMail,
    };
    DataService.update(
      'config',
      {label: 'eq.bookkeeping.forward.summary'},
      {value: newConfig},
    );
  }

  /** Retrieves the configured mail address for the specified journal */
  getJournalMailAddress(journalId) {
    const journalConfig = this.journalConfig[journalId];
    return journalConfig?.mail;
  }

  /** Updates the configuration for the specified journal */
  setJournalMailAddress(journalId, selectedMail) {
    if (!Object.keys(this.journalConfig).includes(journalId)) {
      this.journalConfig[journalId] = {};
    }
    const {ok, mail} = rejectInvalidMail(selectedMail);
    if (!ok) {
      this.bumpMailField('journal', journalId);
      return;
    }
    this.journalConfig[journalId]['mail'] = mail;
    this.updateJournalConfig();
  }

  /** Updates the journal configuration in the database */
  updateJournalConfig() {
    const newConfig = [];
    for (const journalId in this.journalConfig) {
      if (this.journalConfig[journalId]['mail']) {
        newConfig.push({
          journal_id: journalId,
          mail: this.journalConfig[journalId]['mail'],
        });
      }
    }
    DataService.update(
      'config',
      {label: 'eq.bookkeeping.forward.journal'},
      {value: newConfig},
    );
  }
}
